import { useMemo, useState } from "react";
import { C, TIER } from "../../shared/theme";
import { pageColumn, PAGE_WIDTH } from "../../shared/layout";
import { useTwoColumns } from "../../shared/useWideScreen";
import { BottomNav } from "../../shared/BottomNav";
import { TopBar } from "../../shared/TopBar";
import { HELP } from "../../shared/helpContent";
import { Segmented } from "../../shared/Segmented";
import { MonthCalendar } from "../../shared/MonthCalendar";
import { intro } from "../../shared/textStyles";
import { useAddRequest } from "../../shared/useAddRequest";
import { AddButton } from "../../shared/AddButton";
import { groupKeyDates, longDate } from "./keyDates";
import { KeyDateLine } from "./KeyDateCard";
import { useKeyDateCards } from "./useKeyDateCards";
import { useTaskRows } from "../tasks/useTaskRows";

const sectionLabel = () => ({ margin: "0 4px 8px", fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: C.textMid });
const listBox = () => ({ background: C.surface, border: `1px solid ${C.borderCard}`, borderRadius: C.r, overflow: "hidden" });

// Key Dates (Home → Key Dates): what's coming up that the user needs to get ready for.
// `keyDates` is null while loading. Its tasks open and save through the app's task editor (TaskActionsContext).
// `goals` name each date's goal and each task's.
export function KeyDatesPage({ uid, keyDates, loadError, tasks, goals = [], onNav, addRequested, onAddHandled }) {
  const [view, setView] = useState("list");
  // Where two columns fit, the list and the calendar show together, without the switch (Craig, 4 Nov 2026).
  const both = useTwoColumns();
  const taskRows = useTaskRows(goals);
  const { card, editor, setEditing, tasksOf, goalOf } = useKeyDateCards({ uid, tasks, goals, taskRows });
  useAddRequest(addRequested, onAddHandled, () => setEditing("new"));
  const [showPast, setShowPast] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const groups = useMemo(() => groupKeyDates(keyDates || []), [keyDates]);
  const marks = useMemo(() => {
    const m = {};
    (keyDates || []).filter(k => k.date).forEach(k => {
      m[k.date] = { color: TIER.keyDates, label: m[k.date] ? `${m[k.date].label}, ${k.title}` : k.title };
    });
    return m;
  }, [keyDates]);

  const lines = (list, past = false) => (
    <div style={listBox()}>
      {list.map((k, i) => <KeyDateLine key={k.id} keyDate={k} goal={goalOf(k)} tasks={tasksOf(k)} past={past} first={i === 0} onEdit={() => setEditing(k)} />)}
    </div>
  );
  const has = (keyDates || []).length > 0;
  const onDay = selectedDay ? (keyDates || []).filter(k => k.date === selectedDay) : [];

  const list = (
    <>
      {groups.coming.length > 0 && (
        <section>
          <h2 style={sectionLabel()}>Coming up</h2>
          <div style={{ display: "grid", gap: 12 }}>{groups.coming.map(card)}</div>
        </section>
      )}
      {groups.past.length > 0 && (
        <section>
          <h2 style={{ margin: 0 }}>
            <button onClick={() => setShowPast(v => !v)} aria-expanded={showPast}
              style={{ ...sectionLabel(), margin: 0, minHeight: 44, padding: "0 4px", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
              Past · {groups.past.length} <span aria-hidden="true">{showPast ? "▲" : "▼"}</span>
            </button>
          </h2>
          {showPast && lines(groups.past, true)}
        </section>
      )}
    </>
  );
  // The day tapped in the calendar: only its dates, on a phone and a wide screen alike (Craig, 4 Nov 2026: "hide all
  // the dates other than those on the date selected"). Show all dates, or tapping the day again, clears it.
  const day = selectedDay && (
    <section>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <h2 style={{ ...sectionLabel(), margin: "0 4px" }}>{longDate(selectedDay)}</h2>
        <button onClick={() => setSelectedDay(null)}
          style={{ minHeight: 44, padding: "0 4px", background: "none", border: "none", color: C.textGreen, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          Show all dates
        </button>
      </div>
      {onDay.length
        ? <div style={{ display: "grid", gap: 12 }}>{onDay.map(k => (groups.coming.includes(k) ? card(k) : <div key={k.id}>{lines([k], true)}</div>))}</div>
        : <p style={{ ...intro(), margin: "0 4px" }}>Nothing is on that day.</p>}
    </section>
  );
  // On a phone the calendar has its own view, with the day tapped or every date coming up under it. On a wide screen it
  // sits on the left, and the list beside it is what changes.
  const calendar = (
    <section>
      <MonthCalendar marks={marks} selectedDay={selectedDay} onSelectDay={setSelectedDay} />
      {!both && (day || (
        <>
          <h2 style={sectionLabel()}>All dates coming up</h2>
          {groups.coming.length ? lines(groups.coming) : <p style={{ ...intro(), margin: "0 4px" }}>Nothing is coming up. Your key dates have all passed.</p>}
        </>
      ))}
    </section>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", background: C.bg, color: C.text, position: "relative" }}>
      {editor}

      <TopBar title="Key Dates" help={HELP.keydates} />
      {/* Two columns use the shared rules (wideCss), so their edges match Today's. */}
      <div className={both ? "page-columns-scroll" : undefined} style={{ flex: 1, overflowY: "auto", paddingBottom: both ? 96 : 0 }}>
        <div style={{ ...(both ? {} : pageColumn), padding: both ? "4px 0 0" : "4px 16px 96px", display: "grid", gap: 16 }}>

          {keyDates === null && !loadError && (
            <div aria-hidden="true" style={{ display: "grid", gap: 12 }}>
              {[0, 1].map(i => <div key={i} style={{ height: 150, borderRadius: C.r, background: C.surface2 }} />)}
            </div>
          )}

          {loadError && (
            <p role="alert" style={{ ...intro(), margin: "0 4px" }}>
              We couldn't load your key dates. Check your connection, then open this page again.
            </p>
          )}

          {keyDates && !has && (
            <div style={{ padding: "0 4px", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 16 }}>
              <p style={intro()}>You don't have any key dates yet. Add something coming up that you need to get ready for, like a trip, a birthday or a renewal.</p>
              <AddButton onClick={() => setEditing("new")}>Add a key date</AddButton>
            </div>
          )}

          {has && (
            <>
              <p style={{ ...intro(), margin: "0 4px", maxWidth: PAGE_WIDTH }}>
                These are the things coming up that you need to get ready for. Each one shows on Radar, with the tasks still to do, from the day you chose for reminders to start.
              </p>
              {!both && <Segmented label="Show key dates as" stretch options={[["list", "List"], ["calendar", "Calendar"]]} value={view} onChange={(v) => { setView(v); setSelectedDay(null); }} />}

              {both ? (
                <div className="page-columns narrow-first">
                  {/* Craig, 4 Nov 2026: "move the calendar to the left of the list", and 5 Nov: 35% to the list's 65%. It stays
                      in view as the list scrolls. */}
                  <div style={{ position: "sticky", top: 0 }}>{calendar}</div>
                  <div style={{ display: "grid", gap: 16 }}>{day || list}</div>
                </div>
              ) : view === "list" ? list : calendar}
            </>
          )}
        </div>
      </div>
      {taskRows.toast}
      <BottomNav active={null} onNav={onNav} />
    </div>
  );
}
